import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { loginWithCustomToken, getIdToken, auth } from '../firebase';
import { useAppDispatch } from '../hooks/redux';
import { setAuth } from '../store/authSlice';
import { onIdTokenChanged } from 'firebase/auth';

const AuthHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No authentication token provided in URL');
      return;
    }

    const authenticate = async () => {
      try {
        const user = await loginWithCustomToken(token);
        const idToken = await getIdToken();

        if (idToken && user.email) {
          dispatch(
            setAuth({
              token: idToken,
              userId: user.uid,
              email: user.email,
            })
          );

          // Set up token refresh listener
          const unsubscribe = onIdTokenChanged(auth, async (user) => {
            if (user) {
              const freshToken = await user.getIdToken();
              dispatch(
                setAuth({
                  token: freshToken,
                  userId: user.uid,
                  email: user.email || '',
                })
              );
            }
          });

          // Navigate to transactions page
          navigate('/transactions', { replace: true });

          return () => unsubscribe();
        }
      } catch (err: any) {
        console.error('Authentication error:', err);
        setError(err.message || 'Failed to authenticate with provided token');
      }
    };

    authenticate();
  }, [searchParams, navigate, dispatch]);

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <Alert severity="error">{error}</Alert>
        <Typography variant="body2" color="text.secondary">
          Please ensure you have a valid authentication token in the URL
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
      <CircularProgress />
      <Typography variant="body1">Authenticating...</Typography>
    </Box>
  );
};

export default AuthHandler;
